import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

interface ColocaResourceLinkProps {
  /** Visual rendered on the left — typically an icon, avatar, or branded badge. */
  visual: ReactNode;
  /** Small uppercase label above the title (e.g. "Pocket", "Holder"). */
  label: string;
  /** Primary line — the resource name. */
  title: string;
  /** Optional secondary line (e.g. email, count). */
  subtitle?: string;
  /**
   * When provided, the card renders as a `<button>` and shows a trailing chevron. When omitted, it renders as
   * a static `<div>` (no chevron).
   */
  onClick?: () => void;
  className?: string;
}

/**
 * Clickable (or static) card pointing to another resource. Visual + label/title/subtitle + chevron. Use for
 * in-page navigation between related entities — e.g. card → pocket, card → holder.
 *
 * @summary list-row-style link to a related resource
 */
export function ColocaResourceLink({
  visual,
  label,
  title,
  subtitle,
  onClick,
  className,
}: ColocaResourceLinkProps) {
  const body = (
    <>
      {visual}
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-semibold text-foreground">{title}</div>
        {subtitle && <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>}
      </div>
      {onClick && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3.5 text-left shadow-sm transition-colors hover:border-ring/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-2xl border border-border bg-card p-3.5 shadow-sm",
        className,
      )}
    >
      {body}
    </div>
  );
}
